import { useToken } from 'hooks/Tokens'
import React, { useMemo } from 'react'
import { Text } from 'rebass'
import styled, { useTheme } from 'styled-components'
import { PositionDetails } from 'types/position'
import Row, { RowBetween } from 'components/Row'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import { useV3PositionFees } from 'hooks/useV3PositionFees'
import { CurrencyAmount, Fraction } from '@uniswap/sdk-core'
import { usePool } from 'hooks/usePools'
import { formatTokenAmount } from 'utils/formatTokenAmount'
import CurrencyLogo from 'components/CurrencyLogo'
import { getPriceOrderingFromPositionForUI } from 'components/PositionList/PositionListItem'
import { Position } from '@uniswap/v3-sdk'
import { unwrappedToken } from 'utils/wrappedCurrency'
import Tag from 'components/Tag'
import TagRange from 'components/PositionList/PositionListItem/TagRange'
import useUSDCPrice from 'hooks/useUSDCPrice'

const PoolItem = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  width: 100%;
  border-radius: 12px;
  border: 1px solid ${({ theme, isSelected }) => (isSelected ? theme.primary1 : theme.bg1)};
  background-color: ${({ theme, isSelected }) => (isSelected ? theme.bg0 : theme.bg1)};
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  &:hover {
    background-color: ${({ theme }) => theme.bg2};
  }
`
export default function ClaimPositionItem({
  position: positionDetails,
  isSelected,
  onSelect,
}: {
  position: PositionDetails
  isSelected: boolean
  onSelect: () => void
}) {
  const theme = useTheme()
  const {
    token0: token0Address,
    token1: token1Address,
    fee: feeAmount,
    liquidity,
    tickLower,
    tickUpper,
  } = positionDetails
  const token0 = useToken(token0Address)
  const token1 = useToken(token1Address)

  const currency0 = token0 ? unwrappedToken(token0) : undefined
  const currency1 = token1 ? unwrappedToken(token1) : undefined
  const [, pool] = usePool(currency0 ?? undefined, currency1 ?? undefined, feeAmount)
  const [feeValue0, feeValue1] = useV3PositionFees(pool ?? undefined, positionDetails)

  const position = useMemo(() => {
    if (pool) {
      return new Position({ pool, liquidity: liquidity.toString(), tickLower, tickUpper })
    }
    return undefined
  }, [liquidity, pool, tickLower, tickUpper])

  const price0 = useUSDCPrice(token0 ?? undefined)
  const price1 = useUSDCPrice(token1 ?? undefined)

  const fiatValueOfFees: CurrencyAmount | null = useMemo(() => {
    if (!price0 || !price1 || !feeValue0 || !feeValue1) return null
    const amount0 = price0.quote(feeValue0)
    const amount1 = price1.quote(feeValue1)
    return amount0.add(amount1)
  }, [price0, price1, feeValue0, feeValue1])

  // prices
  const { base } = getPriceOrderingFromPositionForUI(position)
  const inverted = token1 ? base?.equals(token1) : undefined
  const currencyQuote = inverted ? currency1 : currency0
  const currencyBase = inverted ? currency0 : currency1

  // check if price is within range
  const isOutOfRange: boolean = pool ? pool.tickCurrent < tickLower || pool.tickCurrent >= tickUpper : false

  const removed = liquidity?.eq(0)

  const tokenID = positionDetails.tokenId.toString()

  if (removed) {
    return null
  }

  return (
    <PoolItem onClick={onSelect} isSelected={isSelected}>
      <RowBetween>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Tag variant={1}>ID:{tokenID}</Tag>
          <TagRange removed={removed} inRange={!isOutOfRange} />
        </div>
        <Checkmark isSelected={isSelected} />
      </RowBetween>
      <Row style={{ gap: '8px', paddingLeft: '18px' }} className="pool-name">
        <DoubleCurrencyLogo currency0={currencyBase} currency1={currencyQuote} size={32} />
        <Text color={theme.text2} fontWeight={400} fontSize={14}>
          {token0?.symbol} / {token1?.symbol}
        </Text>
      </Row>
      <Row>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '12px' }}>
          <span
            style={{
              color: '#c3c5cb',
              fontWeight: 400,
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            Unclaimed fees{' '}
            <span style={{ display: 'flex' }}>
              ({fiatValueOfFees?.greaterThan(new Fraction(1, 100)) ? <>${fiatValueOfFees.toFixed(2)}</> : <> $-</>})
            </span>
          </span>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CurrencyLogo currency={currencyQuote} size={'20px'} />
              <span style={{ fontSize: '12px', fontWeight: 500 }}>
                {inverted
                  ? feeValue1
                    ? formatTokenAmount(feeValue1, 4)
                    : '-'
                  : feeValue0
                  ? formatTokenAmount(feeValue0, 4)
                  : '-'}{' '}
                {currencyQuote?.symbol ? currencyQuote?.symbol : ''}
              </span>
            </div>
            <span style={{ padding: '0 4px' }}>/</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CurrencyLogo currency={currencyBase} size={'20px'} />
              <span style={{ fontSize: '12px', fontWeight: 500 }}>
                {inverted
                  ? feeValue0
                    ? formatTokenAmount(feeValue0, 4)
                    : '-'
                  : feeValue1
                  ? formatTokenAmount(feeValue1, 4)
                  : '-'}{' '}
                {currencyBase?.symbol ? currencyBase?.symbol : ''}
              </span>
            </div>
          </div>
        </div>
      </Row>
    </PoolItem>
  )
}

const Checkmark = ({ isSelected }: { isSelected: boolean }) => {
  return (
    <>
      {isSelected ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="rgb(74, 251, 57)"
          stroke="black"
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon icon-tabler icons-tabler-filled icon-tabler-circle-check"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M17 3.34a10 10 0 1 1 -14.995 8.984l-.005 -.324l.005 -.324a10 10 0 0 1 14.995 -8.336zm-1.293 5.953a1 1 0 0 0 -1.32 -.083l-.094 .083l-3.293 3.292l-1.293 -1.292l-.094 -.083a1 1 0 0 0 -1.403 1.403l.083 .094l2 2l.094 .083a1 1 0 0 0 1.226 0l.094 -.083l4 -4l.083 -.094a1 1 0 0 0 -.083 -1.32z" />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#C3C5CB"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon icon-tabler icons-tabler-outline icon-tabler-circle-check"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
          <path d="M9 12l2 2l4 -4" />
        </svg>
      )}
    </>
  )
}
